import { ArrowLeft, Building2, GraduationCap, HeartHandshake, KeyRound, ShoppingCart, BarChart3 } from "lucide-react";
import { isHotelModeActive, getCachedHotel } from "./hotelClient";

// Organisation Sign In selector page — three tiles, all active.
export default function OrganisationSignIn({ onBack, onGoHotelSignup, onGoHotelActivate, onGoHotelCredits, onGoSectorSignup, onGoUsageReport }) {
  const active = isHotelModeActive();
  const cached = getCachedHotel();

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      <header className="bg-white/80 backdrop-blur-md border-b border-[#E0E0E0] px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button onClick={onBack} className="p-2 rounded-full hover:bg-[#F3F0E6] transition-colors" data-testid="org-back-button">
            <ArrowLeft className="w-6 h-6 text-[#4A4A4A]" />
          </button>
          <h1 className="text-lg sm:text-xl font-semibold text-[#4A4A4A]">Organisation Sign In</h1>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-8 sm:py-12">
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#4A4A4A] mb-2">Choose your organisation type</h2>
          <p className="text-sm text-[#6B6B6B]">Personal families — use the ordinary "Sign in" pill instead.</p>
        </div>

        {/* If already in Hotel Mode show a friendly banner + shortcuts */}
        {active && cached && (
          <div className="mb-6 bg-[#FFF7D6] border-2 border-[#E8D4A8] rounded-2xl p-5" data-testid="org-hotel-active-banner">
            <p className="text-xs font-semibold text-[#7A5A00] uppercase tracking-wide mb-1">Hotel Mode active</p>
            <p className="text-base font-bold text-[#4A4A4A]">{cached.hotel_name}</p>
            <p className="text-xs text-[#6B6B6B] mt-1">
              {cached.total_available?.toLocaleString?.() ?? 0} credits available · {cached.membership_tier}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={onGoHotelCredits} data-testid="org-open-hotel-credits" className="text-xs px-3 py-1.5 rounded-full bg-[#4A4A4A] text-white hover:bg-black transition-all">
                Open Hotel Membership &amp; Chat Credits
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Hotels — active */}
          <div className="bg-white rounded-2xl border-2 border-[#E8D4A8] p-6 hover:shadow-lg transition-all">
            <Building2 className="w-9 h-9 mb-3 text-[#B89823]" />
            <p className="text-xs font-semibold text-[#B89823] uppercase tracking-wide mb-1">Available now</p>
            <h3 className="text-lg font-bold text-[#4A4A4A] mb-1">Hotels &amp; Kids' Clubs</h3>
            <p className="text-xs text-[#6B6B6B] mb-4">Shared-iPad memberships with a central credit balance across every venue.</p>
            <div className="space-y-2">
              <button
                onClick={() => onGoHotelActivate && onGoHotelActivate("hotel")}
                data-testid="org-hotel-enter-code"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#4A4A4A] text-white rounded-full font-semibold text-sm hover:bg-black transition-all"
              >
                <KeyRound className="w-4 h-4" /> Enter activation code
              </button>
              <button
                onClick={() => onGoHotelSignup && onGoHotelSignup()}
                data-testid="org-hotel-buy-membership"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#E8D4A8] text-[#5A4A32] rounded-full font-semibold text-sm hover:bg-[#D9C599] transition-all"
              >
                <ShoppingCart className="w-4 h-4" /> Buy a hotel membership
              </button>
              <button
                onClick={() => onGoHotelSignup && onGoHotelSignup("addons")}
                data-testid="org-hotel-buy-addons"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-[#5A4A32] rounded-full font-semibold text-sm hover:bg-[#F8F1DE] transition-all border-2 border-[#E8D4A8]"
              >
                <ShoppingCart className="w-4 h-4" /> Buy add-on credits
              </button>
              <button
                onClick={() => onGoUsageReport && onGoUsageReport("hotel")}
                data-testid="org-hotel-usage-report"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#FFF7D6] text-[#5A4A32] rounded-full font-semibold text-sm hover:bg-[#F5E9BF] transition-all border-2 border-[#E8D4A8]"
              >
                <BarChart3 className="w-4 h-4" /> View usage report
              </button>
            </div>
          </div>

          {/* Schools — active */}
          <div className="bg-white rounded-2xl border-2 border-[#B8CFE3] p-6 hover:shadow-lg transition-all" data-testid="org-tile-schools">
            <GraduationCap className="w-9 h-9 mb-3 text-[#5A87B8]" />
            <p className="text-xs font-semibold text-[#5A87B8] uppercase tracking-wide mb-1">Available now</p>
            <h3 className="text-lg font-bold text-[#4A4A4A] mb-1">Schools</h3>
            <p className="text-xs text-[#6B6B6B] mb-4">SEND &amp; primary classroom licensing — one shared credit balance across every classroom.</p>
            <div className="space-y-2">
              <button
                onClick={() => onGoHotelActivate && onGoHotelActivate("school")}
                data-testid="org-school-enter-code"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#4A4A4A] text-white rounded-full font-semibold text-sm hover:bg-black transition-all"
              >
                <KeyRound className="w-4 h-4" /> Enter activation code
              </button>
              <button
                onClick={() => onGoSectorSignup && onGoSectorSignup("school")}
                data-testid="org-school-buy-membership"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#B8CFE3] text-[#32475A] rounded-full font-semibold text-sm hover:bg-[#A2BED9] transition-all"
              >
                <ShoppingCart className="w-4 h-4" /> Buy a school membership
              </button>
              <button
                onClick={() => onGoSectorSignup && onGoSectorSignup("school", "addons")}
                data-testid="org-school-buy-addons"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-[#32475A] rounded-full font-semibold text-sm hover:bg-[#F1F5FA] transition-all border-2 border-[#B8CFE3]"
              >
                <ShoppingCart className="w-4 h-4" /> Buy add-on credits
              </button>
              <button
                onClick={() => onGoUsageReport && onGoUsageReport("school")}
                data-testid="org-school-usage-report"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#E6F0F9] text-[#32475A] rounded-full font-semibold text-sm hover:bg-[#D0E1F1] transition-all border-2 border-[#B8CFE3]"
              >
                <BarChart3 className="w-4 h-4" /> View usage report
              </button>
            </div>
          </div>

          {/* Charities — active */}
          <div className="bg-white rounded-2xl border-2 border-[#E3B8CF] p-6 hover:shadow-lg transition-all" data-testid="org-tile-organisations">
            <HeartHandshake className="w-9 h-9 mb-3 text-[#B85A87]" />
            <p className="text-xs font-semibold text-[#B85A87] uppercase tracking-wide mb-1">Available now</p>
            <h3 className="text-lg font-bold text-[#4A4A4A] mb-1">Charities</h3>
            <p className="text-xs text-[#6B6B6B] mb-4">Discounted access for children's charities, hospices, hospitals and community groups.</p>
            <div className="space-y-2">
              <button
                onClick={() => onGoHotelActivate && onGoHotelActivate("charity")}
                data-testid="org-charity-enter-code"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#4A4A4A] text-white rounded-full font-semibold text-sm hover:bg-black transition-all"
              >
                <KeyRound className="w-4 h-4" /> Enter activation code
              </button>
              <button
                onClick={() => onGoSectorSignup && onGoSectorSignup("charity")}
                data-testid="org-charity-buy-membership"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#E3B8CF] text-[#5A324A] rounded-full font-semibold text-sm hover:bg-[#D9A2BE] transition-all"
              >
                <ShoppingCart className="w-4 h-4" /> Buy a charity membership
              </button>
              <button
                onClick={() => onGoSectorSignup && onGoSectorSignup("charity", "addons")}
                data-testid="org-charity-buy-addons"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-[#5A324A] rounded-full font-semibold text-sm hover:bg-[#FAF1F5] transition-all border-2 border-[#E3B8CF]"
              >
                <ShoppingCart className="w-4 h-4" /> Buy add-on credits
              </button>
              <button
                onClick={() => onGoUsageReport && onGoUsageReport("charity")}
                data-testid="org-charity-usage-report"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#FAF1F5] text-[#5A324A] rounded-full font-semibold text-sm hover:bg-[#F0DEE7] transition-all border-2 border-[#E3B8CF]"
              >
                <BarChart3 className="w-4 h-4" /> View usage report
              </button>
            </div>
          </div>
        </div>
      </main>

      <footer className="py-4 px-4 text-center">
        <p className="text-xs text-[#9A9A9A]">Personal families — please use the ordinary Sign In pill.</p>
      </footer>
    </div>
  );
}
